import { PAUSED_UPLOAD_TTL_HOURS } from "@/components/UploadProgress/UploadProgressLoadingCard";
import { DemoUploadCard } from "../demo/DemoUploadCard";
import { ArticleBody, ArticleCallout, ArticleDemoFrame, ArticleTable, ArticleTableWrap } from "./Article.elements";

const DECISIONS = [
  ["Bar moves slowly", "Wait. Speed = your connection. Nothing is stuck."],
  ["\"Reconnecting…\" / \"stalled — retrying automatically\"", "Wait. It retries by itself and continues from the last chunk."],
  ["Offline for a while, card says paused", "Wait or press play when you have signal. It auto-resumes up to 3 times."],
  ["Need to move / close the laptop", "Press the pause icon (or just close the tab). Resume within " + PAUSED_UPLOAD_TTL_HOURS + " hours."],
  ["Reloaded the page by accident", "Select the same files again on the card. Already-uploaded chunks are skipped."],
  ["Package shows \"another device\"", "Press Take over here on this device. Do not cancel."],
  ["Wrong files, wrong package, must not be delivered", "Cancel. This is what Cancel is for."],
];

export const CancelVsContinueArticle = () => (
  <ArticleBody>
    <h3>Cancel is a delivery decision, not a repair tool</h3>
    <p>
      <strong>Cancel</strong> means &ldquo;I don&apos;t want this package delivered.&rdquo; It asks for a short
      reason and then <strong>deletes the package on the server</strong>, including every chunk already uploaded.
      Pressing it because the bar looks slow or a banner says <em>retrying</em> only makes you start again from
      zero — on the same connection.
    </p>

    <ArticleDemoFrame>
      <DemoUploadCard state="stalled" expandFiles={false} />
    </ArticleDemoFrame>
    <p>
      The card above looks alarming but is healthy: the uploader has noticed the silence, is already retrying, and
      will pick up from the last confirmed chunk. The right response is to wait — or, if you know the network is
      gone for a while, to pause.
    </p>

    <h3>Pause instead</h3>
    <p>
      The icon on the left of the card is a <strong>pause / play button</strong>. Pausing keeps everything on the
      server for <strong>{PAUSED_UPLOAD_TTL_HOURS} hours</strong>. Walk to better Wi-Fi, drive back to the office,
      plug into Ethernet — then press play. Closing the tab works too: the card is back when you reopen the app.
    </p>

    <h3>Decision list</h3>
    <ArticleTableWrap>
      <ArticleTable>
        <thead>
          <tr>
            <th>Situation</th>
            <th>Do this</th>
          </tr>
        </thead>
        <tbody>
          {DECISIONS.map(([situation, action]) => (
            <tr key={situation}>
              <td>{situation}</td>
              <td>{action}</td>
            </tr>
          ))}
        </tbody>
      </ArticleTable>
    </ArticleTableWrap>

    <ArticleCallout tone="warning">
      If something genuinely looks broken (hours with no movement on a good connection, a red <em>Failed</em>
      state that Retry doesn&apos;t clear), don&apos;t cancel — copy the <strong>support snapshot</strong> from the
      footer and send it to support. It contains the upload id and lets us fix it without you re-uploading.
    </ArticleCallout>
  </ArticleBody>
);
